import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import InputGroup from 'react-bootstrap/InputGroup';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import Card from 'react-bootstrap/Card';
import SubtleBadge from '@/components/common/SubtleBadge';
import Icon from './Icon';
import { CLUSTER_MODES } from '../utils/recluster';
import VolumeThresholdLegend from './VolumeThresholdLegend';
import type { IconName } from './Icon';
import type { ClusterModeId, VolumeThresholds } from '../types';

const treeBtnClass =
  'bg-100 d-inline-flex align-items-center justify-content-center p-2 text-700 border border-400 rounded-3 lh-1';

type IconTipButtonProps = {
  id: string;
  title: string;
  icon: IconName;
  onClick?: () => void;
  disabled?: boolean;
  active?: boolean;
};

type ClusterToolbarProps = {
  query: string;
  onQueryChange: (value: string) => void;
  matchCount: number;
  onExpandAll: () => void;
  onCollapseAll: () => void;
  onFitView: () => void;
  onClearSelection: () => void;
  hasSelection: boolean;
  statusMessage?: string;
  depthFilter: number | null;
  onDepthFilterChange?: (value: number | null) => void;
  maxDepth?: number;
  compact: boolean;
  onToggleCompact: () => void;
  showBadges: boolean;
  onToggleBadges: () => void;
  onUndo: () => void;
  canUndo: boolean;
  onResetLayout: () => void;
  onOpenCompare: () => void;
  presentation: boolean;
  onTogglePresentation: () => void;
  onPresentationNext: () => void;
  onPresentationPrev: () => void;
  presentationStep: number;
  presentationMax: number;
  locked?: boolean;
  onToggleLock: () => void;
  clusterMode?: ClusterModeId | string;
  onClusterModeChange?: (mode: ClusterModeId | string) => void;
  clusterSummary?: string | null;
  thresholds?: VolumeThresholds | null;
};

function IconTipButton({
  id,
  title,
  icon,
  onClick,
  disabled = false,
  active = false
}: IconTipButtonProps) {
  return (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip id={`tip-${id}`}>{title}</Tooltip>}
    >
      <Button
        type="button"
        size="sm"
        variant="link"
        className={`${treeBtnClass}${active ? ' text-primary border-primary' : ''}`}
        onClick={onClick}
        disabled={disabled}
        aria-label={title}
      >
        <Icon name={icon} className="fs--1" />
      </Button>
    </OverlayTrigger>
  );
}

export default function ClusterToolbar({
  query,
  onQueryChange,
  matchCount,
  onExpandAll,
  onCollapseAll,
  onFitView,
  onClearSelection,
  hasSelection,
  statusMessage = '',
  depthFilter,
  onDepthFilterChange,
  maxDepth = 4,
  compact,
  onToggleCompact,
  showBadges,
  onToggleBadges,
  onUndo,
  canUndo,
  onResetLayout,
  onOpenCompare,
  presentation,
  onTogglePresentation,
  onPresentationNext,
  onPresentationPrev,
  presentationStep,
  presentationMax,
  locked = false,
  onToggleLock,
  clusterMode = 'default',
  onClusterModeChange,
  clusterSummary = '',
  thresholds = null
}: ClusterToolbarProps) {
  const showFooter = Boolean(thresholds) || Boolean(clusterSummary);

  return (
    <Card className="mb-0" dir="rtl">
      <Card.Body className="py-3 px-3">
        <div className="d-flex flex-wrap align-items-center gap-2">
          <div className="cluster-toolbar__filters d-flex flex-wrap align-items-center gap-2">
            <InputGroup size="sm" style={{ maxWidth: 280, minWidth: 180 }}>
              <InputGroup.Text>
                <Icon name="search" />
              </InputGroup.Text>
              <Form.Control
                type="search"
                placeholder="جستجوی کلمه یا خوشه..."
                value={query}
                onChange={e => onQueryChange(e.target.value)}
                aria-label="جستجو در درخت"
              />
              {query ? (
                <Button
                  variant="outline-secondary"
                  onClick={() => onQueryChange('')}
                  aria-label="پاک کردن جستجو"
                >
                  <Icon name="x-lg" />
                </Button>
              ) : null}
            </InputGroup>

            <Form.Select
              size="sm"
              className="w-auto flex-shrink-0"
              style={{ minWidth: 130 }}
              value={depthFilter ?? ''}
              disabled={locked}
              onChange={e =>
                onDepthFilterChange?.(
                  e.target.value === '' ? null : Number(e.target.value)
                )
              }
              aria-label="فیلتر عمق"
            >
              <option value="">همه عمق‌ها</option>
              {Array.from({ length: Math.max(1, maxDepth + 1) }, (_, i) => (
                <option key={i} value={i}>
                  تا عمق {i}
                </option>
              ))}
            </Form.Select>

            <Form.Select
              size="sm"
              className="w-auto flex-shrink-0"
              style={{ minWidth: 150 }}
              value={clusterMode}
              disabled={locked}
              onChange={e => onClusterModeChange?.(e.target.value)}
              aria-label="کلاستر بر اساس"
            >
              {CLUSTER_MODES.map(mode => (
                <option key={mode.id} value={mode.id}>
                  {mode.label}
                </option>
              ))}
            </Form.Select>

            {query ? (
              <SubtleBadge bg={matchCount ? 'success' : 'secondary'} pill>
                {matchCount ? `${matchCount} نتیجه` : 'نتیجه‌ای نیست'}
              </SubtleBadge>
            ) : null}

            {statusMessage ? (
              <SubtleBadge bg="success" pill>
                {statusMessage}
              </SubtleBadge>
            ) : null}

            {locked ? (
              <SubtleBadge bg="warning" pill>
                قفل فعال
              </SubtleBadge>
            ) : null}
          </div>

          <div className="cluster-toolbar__actions d-flex flex-wrap align-items-center gap-1 ms-auto">
            <IconTipButton
              id="expand"
              title="باز کردن همه نودها"
              icon="arrows-expand"
              onClick={onExpandAll}
              disabled={locked}
            />
            <IconTipButton
              id="collapse"
              title="جمع کردن نودها"
              icon="arrows-collapse"
              onClick={onCollapseAll}
              disabled={locked}
            />
            <IconTipButton
              id="undo"
              title="برگرداندن"
              icon="arrow-counterclockwise"
              onClick={onUndo}
              disabled={locked || !canUndo}
            />
            <IconTipButton
              id="fit"
              title="فیت صفحه (0)"
              icon="aspect-ratio"
              onClick={onFitView}
            />
            <IconTipButton
              id="reset"
              title="بازنشانی جای نودها"
              icon="arrow-repeat"
              onClick={onResetLayout}
              disabled={locked}
            />
            {hasSelection ? (
              <IconTipButton
                id="clear"
                title="پاک کردن انتخاب"
                icon="x-circle"
                onClick={onClearSelection}
              />
            ) : null}
            <IconTipButton
              id="compact"
              title="حالت فشرده"
              icon="arrows-angle-contract"
              onClick={onToggleCompact}
              active={compact}
            />
            <IconTipButton
              id="badges"
              title={showBadges ? 'مخفی کردن نشان‌ها' : 'نمایش نشان‌ها'}
              icon="tags"
              onClick={onToggleBadges}
              active={showBadges}
            />
            <IconTipButton
              id="lock"
              title={locked ? 'باز کردن قفل (L)' : 'قفل کردن (L)'}
              icon={locked ? 'unlock' : 'lock'}
              onClick={onToggleLock}
              active={locked}
            />
            <IconTipButton
              id="compare"
              title="مقایسه دو کلاستر"
              icon="subtract"
              onClick={onOpenCompare}
            />
            <IconTipButton
              id="presentation"
              title="ارائه"
              icon="play-btn"
              onClick={onTogglePresentation}
              disabled={locked}
              active={presentation}
            />
            {presentation ? (
              <>
                <IconTipButton
                  id="prev"
                  title="مرحله قبل"
                  icon="skip-backward"
                  onClick={onPresentationPrev}
                  disabled={locked || presentationStep <= 0}
                />
                <Button
                  size="sm"
                  variant="link"
                  disabled
                  className={`${treeBtnClass} fs--1 px-2`}
                >
                  {presentationStep}/{presentationMax}
                </Button>
                <IconTipButton
                  id="next"
                  title="مرحله بعد"
                  icon="skip-forward"
                  onClick={onPresentationNext}
                  disabled={locked || presentationStep >= presentationMax}
                />
              </>
            ) : null}
          </div>
        </div>

        {showFooter ? (
          <div className="d-flex flex-wrap align-items-center gap-2 mt-2 pt-2 border-top border-200">
            {thresholds ? (
              <VolumeThresholdLegend thresholds={thresholds} />
            ) : (
              <span className="fs--2 text-600">{clusterSummary}</span>
            )}
          </div>
        ) : null}
      </Card.Body>
    </Card>
  );
}
